import { useRef, useState } from 'react';
import type { MantineSize } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { toDateTimeValue } from '../../../utils/dateHelpers';
import { calendarMinDate, dateTimeAfterChange, shownTime } from './dateTimeAfterChange';

export interface DateTimeFieldProps {
  label?: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder?: string;
  clearable?: boolean;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  error?: string | boolean;
  disabled?: boolean;
  size?: MantineSize;
  style?: React.CSSProperties;
  /**
   * MED-1350: the time ("HH:mm") a date pick gives when the field has no
   * value yet, for example '09:00' on a start field. A time the user picks or
   * types replaces it. It never replaces a time the field already has.
   */
  defaultTimeOnDatePick?: string;
  /**
   * MED-1360: the month the calendar opens on while the field is empty, for
   * example the start on an end field. Without it the calendar opens on today.
   */
  defaultDate?: Date;
  /**
   * MED-1360: the value of a date pick into the empty field, for example an
   * end that moves to the next day when the start plus its length passes
   * midnight. Without it the pick gets defaultTimeOnDatePick.
   */
  valueOnFirstDatePick?: (picked: Date) => Date;
}

/**
 * MED-1360: the calendar dropdown height with its time row (371 px at the
 * default size), plus Mantine's 8 px offset from the field.
 */
const CALENDAR_SPACE_PX = 380;

export function DateTimeField({
  label,
  value,
  onChange,
  placeholder = 'Select date and time',
  clearable = true,
  required,
  minDate,
  maxDate,
  error,
  disabled,
  size,
  style,
  defaultTimeOnDatePick,
  defaultDate,
  valueOnFirstDatePick,
}: DateTimeFieldProps) {
  // MED-1350: Mantine reads defaultTimeValue into its time box only when it
  // mounts. A new mount after the field empties shows the default time again,
  // so one hour click after a date pick gives a full time.
  const emptiedCount = useRef(0);
  const hadValue = useRef(value !== null);
  if (hadValue.current && value === null) emptiedCount.current += 1;
  hadValue.current = value !== null;

  // MED-1360: the applied time always equals the time the box shows. Mantine
  // applies its own internal time on a date pick, which a time box click can
  // leave empty (12:00 AM) and a mount can leave stale. A date pick now takes
  // the shown time. Mantine reports a time box edit to the time picker's
  // onChange first and then to handleChange in the same call, so this flag
  // marks that change as the user's time.
  const fromTimeBox = useRef(false);
  const handleTimeBoxChange = (timeString: string) => {
    fromTimeBox.current = timeString !== '';
    queueMicrotask(() => {
      fromTimeBox.current = false;
    });
  };
  const handleChange = (rawValue: string | null) => {
    const next = dateTimeAfterChange({
      previous: value,
      next: toDateTimeValue(rawValue),
      fromTimeBox: fromTimeBox.current,
      defaultTime: defaultTimeOnDatePick,
      onFirstDatePick: valueOnFirstDatePick,
    });
    fromTimeBox.current = false;
    onChange(next);
  };
  const boxTime = shownTime(value, defaultTimeOnDatePick);

  // MED-1360: Mantine runs shift before flip, so an always-on vertical shift
  // stopped the flip and put the calendar over its own field. The shift is
  // on only when the window holds the calendar neither above nor below the
  // field. The check runs on the click that opens the calendar.
  const [noRoomAboveOrBelow, setNoRoomAboveOrBelow] = useState(false);
  const handleFieldClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const roomBelow = window.innerHeight - rect.bottom;
    setNoRoomAboveOrBelow(Math.max(rect.top, roomBelow) < CALENDAR_SPACE_PX);
  };

  return (
    <DateTimePicker
      key={emptiedCount.current}
      label={label}
      placeholder={placeholder}
      valueFormat="MM/DD/YYYY hh:mm A"
      value={value}
      onChange={handleChange}
      clearable={clearable}
      required={required}
      // MED-1360: the day only, so Mantine never replaces a typed time.
      minDate={calendarMinDate(minDate)}
      maxDate={maxDate}
      error={error}
      disabled={disabled}
      size={size}
      style={style}
      // Mantine uses this only when its own time is empty, so a field that
      // already has a time keeps it on a date change.
      defaultTimeValue={value ? undefined : defaultTimeOnDatePick}
      defaultDate={value ? undefined : defaultDate}
      onClick={handleFieldClick}
      // MED-1360: with no room above or below, a vertical shift keeps the
      // whole calendar inside the window. Otherwise the calendar flips as usual.
      popoverProps={
        noRoomAboveOrBelow ? { middlewares: { shift: { crossAxis: true, padding: 8 } } } : undefined
      }
      timePickerProps={{
        // MED-1360: Mantine empties its time box each time the dropdown closes
        // with no value, so a reopen showed "--:-- --" while a date pick still
        // gave the default time. The box shows the field's own time, or the
        // default time while the field is empty. A date pick applies the same.
        ...(boxTime !== undefined ? { value: boxTime } : {}),
        onChange: handleTimeBoxChange,
        format: '12h',
        withDropdown: true,
        // MED-1350: keep the hour list inside the date dropdown. In a portal
        // it sits outside, so a mousedown on an hour reads as an outside click.
        // The date dropdown then closes and the hour click is lost.
        popoverProps: { withinPortal: false },
      }}
    />
  );
}
